import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getActiveItemScrollLeft } from 'src/components/molecules/HorizontalScroller/getActiveItemScrollLeft';

// Treat sub-pixel rounding as "fully scrolled" so the end-of-strip fade/arrow
// don't flicker on fractional scroll positions.
const SCROLL_TOLERANCE_PX = 1;
// Each arrow click advances by most of a viewport, leaving a sliver of overlap
// for context (mirrors the design system's PillBar carousel paging feel).
const PAGE_FRACTION = 0.8;

export interface HorizontalScroll<T extends HTMLElement> {
  canScrollLeft: boolean;
  canScrollRight: boolean;
  scrollNext: () => void;
  scrollPrev: () => void;
  scrollerRef: RefObject<T>;
}

/**
 * Tracks how far a horizontally-scrollable element can scroll in each direction
 * and exposes paged scrolling. Drives the desktop scroll arrows and the
 * edge-fade affordances; on touch/mobile the element is simply swiped and these
 * flags still gate the fades.
 *
 * `recomputeKey` should change whenever the scrollable content changes (e.g. the
 * item list), so the can-scroll flags are re-derived even when the element's own
 * box size is unchanged.
 *
 * `onFirstScroll` is called the first time the strip moves, by swipe or by arrow,
 * and never again for the life of the component.
 *
 * `resetKey` should be a stable identifier of the rendered content (e.g. the
 * current taxonomy level + selection). When it changes the strip brings its
 * active item (`[aria-current="page"]`) into view with the least movement,
 * keeping the position when it is already visible and falling back to the start
 * of the strip when there is no active item. This stops the strip from lingering
 * in a stale, partially-scrolled position after its content changes. Pass
 * `undefined` to opt out of repositioning.
 */
export const useHorizontalScroll = <T extends HTMLElement>(
  recomputeKey?: unknown,
  resetKey?: unknown,
  onFirstScroll?: () => void,
): HorizontalScroll<T> => {
  const scrollerRef = useRef<T>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const hasReportedScroll = useRef(false);

  // Held in a ref so a caller passing an inline callback does not re-subscribe
  // the scroll listener on every render.
  const onFirstScrollRef = useRef(onFirstScroll);
  onFirstScrollRef.current = onFirstScroll;

  const reportFirstScroll = useCallback(() => {
    if (hasReportedScroll.current) {
      return;
    }

    hasReportedScroll.current = true;
    onFirstScrollRef.current?.();
  }, []);

  const update = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) {
      return;
    }

    const { clientWidth, scrollLeft, scrollWidth } = el;
    setCanScrollLeft(scrollLeft > SCROLL_TOLERANCE_PX);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - SCROLL_TOLERANCE_PX);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) {
      return undefined;
    }

    const handleScroll = () => {
      update();
      reportFirstScroll();
    };

    update();
    el.addEventListener('scroll', handleScroll, { passive: true });

    // Prefer ResizeObserver (catches content/layout changes); fall back to the
    // window resize event in environments without it.
    const hasResizeObserver = typeof ResizeObserver !== 'undefined';
    const resizeObserver = hasResizeObserver ? new ResizeObserver(update) : undefined;
    if (resizeObserver) {
      resizeObserver.observe(el);
    } else {
      window.addEventListener('resize', update);
    }

    return () => {
      el.removeEventListener('scroll', handleScroll);
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', update);
      }
    };
  }, [update, recomputeKey, reportFirstScroll]);

  const repositionToActive = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) {
      return;
    }

    const active = el.querySelector<HTMLElement>('[aria-current="page"]');
    el.scrollLeft = getActiveItemScrollLeft(el, active);
    update();
  }, [update]);

  // Reposition when the rendered content changes, not on every render, so the
  // strip never lingers in a stale scroll position, yet a user's own scrolling
  // within an unchanged list is left untouched.
  useEffect(() => {
    if (resetKey === undefined) {
      return;
    }

    repositionToActive();
  }, [repositionToActive, resetKey]);

  const scrollByPage = useCallback(
    (direction: 1 | -1) => {
      const el = scrollerRef.current;
      if (!el) {
        return;
      }

      // Reported here as well as from the scroll listener: a smooth programmatic
      // scroll does settle into scroll events, but the arrow is a deliberate move
      // and should not depend on them arriving.
      reportFirstScroll();
      el.scrollBy({ left: direction * el.clientWidth * PAGE_FRACTION, behavior: 'smooth' });
    },
    [reportFirstScroll],
  );

  const scrollNext = useCallback(() => scrollByPage(1), [scrollByPage]);
  const scrollPrev = useCallback(() => scrollByPage(-1), [scrollByPage]);

  return { canScrollLeft, canScrollRight, scrollNext, scrollPrev, scrollerRef };
};
